<template>
  <div>
    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-2 text-muted">Cargando próximos días…</p>
    </div>

    <div v-else-if="error && !dias.length" class="alert alert-danger">{{ error }}</div>

    <div v-else-if="!dias.length" class="alert alert-info">
      <i class="bi bi-info-circle me-2"></i>
      Aún no hay un menú aceptado para los próximos días.
      <span v-if="isAdmin"> Genéralo en la pestaña Agente y pulsa <strong>Aceptar menú</strong>.</span>
    </div>

    <div v-else class="card shadow-sm proximos-dias">
      <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
        <span class="fw-semibold">
          <i class="bi bi-calendar-week me-2"></i>Próximos días
        </span>
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <small v-if="actualizadoAt" class="text-muted">
            Actualizado {{ formatFechaHora(actualizadoAt) }}
          </small>
          <input
            id="mostrar-dias-anteriores"
            v-model="mostrarAnteriores"
            class="btn-check"
            type="checkbox"
            autocomplete="off"
            aria-label="Mostrar las comidas de los 10 días anteriores"
          />
          <label
            class="btn btn-sm btn-outline-secondary"
            for="mostrar-dias-anteriores"
            title="Mostrar las comidas de los 10 días anteriores"
          >
            <i class="bi bi-clock-history me-1"></i>10 días anteriores
          </label>
          <router-link class="btn btn-sm btn-outline-success" to="/lista-compra">
            <i class="bi bi-cart3 me-1"></i>Lista de la compra
          </router-link>
          <button
            type="button"
            class="btn btn-sm"
            :class="editando ? 'btn-outline-secondary' : 'btn-outline-primary'"
            @click="toggleEditar"
          >
            <i class="bi" :class="editando ? 'bi-x' : 'bi-pencil'"></i>
            {{ editando ? 'Cancelar' : 'Editar' }}
          </button>
        </div>
      </div>
      <div v-if="error" class="alert alert-danger rounded-0 mb-0">{{ error }}</div>
      <div class="card-body">
        <p v-if="!diasVisibles.length" class="text-muted small mb-0">
          No hay comidas {{ mostrarAnteriores ? 'en los últimos 10 días' : 'desde hoy' }}.
          <span v-if="!mostrarAnteriores && diasAnterioresCount">
            Marca <strong>10 días anteriores</strong> para ver {{ diasAnterioresCount }}
            {{ diasAnterioresCount === 1 ? 'día previo' : 'días previos' }}.
          </span>
        </p>

        <template v-else>
          <p
            v-if="editando && diasVisibles.length"
            class="proximos-dnd-ayuda"
            aria-live="polite"
          >
            <span v-if="intercambiando" class="spinner-border spinner-border-sm me-1" role="status"></span>
            <i v-else class="bi bi-arrows-move me-1" aria-hidden="true"></i>
            {{ textoAyuda }}
          </p>

          <div
            v-if="aviso"
            class="alert alert-success py-2 px-3 small d-flex align-items-center justify-content-between flex-wrap gap-2"
            role="status"
          >
            <span>{{ aviso }}</span>
            <button
              v-if="ultimoIntercambio"
              type="button"
              class="btn btn-sm btn-outline-success"
              :disabled="intercambiando"
              @click="deshacer"
            >
              Deshacer
            </button>
          </div>

          <div class="d-none d-md-block table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Plan</th>
                  <th>Desayuno</th>
                  <th>Comida</th>
                  <th>Cena</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="dia in diasVisibles"
                  :key="`tabla-${dia.fecha}`"
                  :class="{
                    'table-primary': dia.fecha === hoy,
                    'is-pasado': esPasado(dia),
                  }"
                >
                  <td class="text-nowrap">
                    <div class="fw-semibold text-capitalize">
                      {{ dia.weekday }}
                      <span v-if="dia.fecha === hoy" class="badge bg-primary ms-1">Hoy</span>
                      <span v-else-if="esPasado(dia)" class="badge bg-light text-muted border ms-1">Anterior</span>
                    </div>
                    <small class="text-muted">{{ formatFecha(dia.fecha) }}</small>
                    <span v-if="dia.esFinDeSemana" class="badge bg-secondary ms-1">Finde</span>
                  </td>
                  <td class="small">{{ dia.planNombre }}</td>
                  <td>
                    <NutricionPlatoLink :texto="dia.desayuno" :href="dia.desayunoHref || ''" />
                  </td>
                  <td v-for="tipo in tiposPlato" :key="`${dia.fecha}-${tipo}`">
                    <NutricionPlatoSlot
                      :fecha="dia.fecha"
                      :tipo="tipo"
                      :movible="mostrarAsa(dia)"
                      :solo-asa="editando"
                      :bloqueado="intercambiando"
                      :origen="esOrigen(dia.fecha, tipo)"
                      :destino="esDestino(dia.fecha, tipo)"
                      :posible="esPosible(dia, tipo)"
                      :aria-label="etiquetaArrastre(dia, tipo)"
                      @iniciar="onIniciar(dia, tipo, $event)"
                      @mover="onMover"
                      @soltar="onSoltar(dia, tipo, $event)"
                      @cancelar-puntero="onCancelar"
                      @activar="onActivar(dia, tipo)"
                    >
                      <div v-if="editando && !esPasado(dia)" class="d-flex gap-1">
                        <input
                          class="form-control form-control-sm"
                          v-model="dia[tipo]"
                          :aria-label="tipo === 'cena' ? 'Cena' : 'Comida'"
                        />
                        <button
                          type="button"
                          class="btn btn-sm"
                          :class="dia[`${tipo}Congelada`] ? 'btn-info' : 'btn-outline-secondary'"
                          :aria-pressed="!!dia[`${tipo}Congelada`]"
                          title="Marcar como congelado (avisa el día antes)"
                          aria-label="Marcar como congelado"
                          @click="dia[`${tipo}Congelada`] = !dia[`${tipo}Congelada`]"
                        >
                          <i class="bi bi-snow" aria-hidden="true"></i>
                        </button>
                      </div>
                      <template v-else>
                        <NutricionPlatoLink :texto="dia[tipo]" :href="dia[`${tipo}Href`] || ''" />
                        <i
                          v-if="dia[`${tipo}Congelada`]"
                          class="bi bi-snow text-info ms-1"
                          title="Congelado"
                          aria-label="Congelado"
                        ></i>
                      </template>
                    </NutricionPlatoSlot>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-md-none proximos-dias-movil">
            <article
              v-for="dia in diasVisibles"
              :key="`card-${dia.fecha}`"
              class="proximos-dia-card"
              :class="{ 'is-hoy': dia.fecha === hoy, 'is-pasado': esPasado(dia) }"
            >
              <header class="proximos-dia-header">
                <div class="min-w-0">
                  <div class="fw-semibold text-capitalize d-flex align-items-center flex-wrap gap-1">
                    {{ dia.weekday }}
                    <span v-if="dia.fecha === hoy" class="badge bg-primary">Hoy</span>
                    <span v-else-if="esPasado(dia)" class="badge bg-light text-muted border">Anterior</span>
                    <span v-if="dia.esFinDeSemana" class="badge bg-secondary">Finde</span>
                  </div>
                  <small class="text-muted">{{ formatFecha(dia.fecha) }}</small>
                </div>
                <div v-if="dia.planNombre" class="proximos-dia-plan">{{ dia.planNombre }}</div>
              </header>
              <div v-if="dia.desayuno" class="proximos-dia-plato">
                <span class="proximos-dia-label">
                  <i class="bi bi-cup-hot me-1" aria-hidden="true"></i>
                  Desayuno
                </span>
                <NutricionPlatoLink :texto="dia.desayuno" :href="dia.desayunoHref || ''" />
              </div>
              <div
                v-for="tipo in tiposPlato"
                :key="`${dia.fecha}-m-${tipo}`"
                class="proximos-dia-plato"
              >
                <span class="proximos-dia-label">
                  <i class="bi me-1" :class="tipo === 'cena' ? 'bi-moon' : 'bi-sun'" aria-hidden="true"></i>
                  {{ tipo === 'cena' ? 'Cena' : 'Comida' }}
                </span>
                <NutricionPlatoSlot
                  :fecha="dia.fecha"
                  :tipo="tipo"
                  :movible="mostrarAsa(dia)"
                  :solo-asa="editando"
                  :bloqueado="intercambiando"
                  :origen="esOrigen(dia.fecha, tipo)"
                  :destino="esDestino(dia.fecha, tipo)"
                  :posible="esPosible(dia, tipo)"
                  :aria-label="etiquetaArrastre(dia, tipo)"
                  @iniciar="onIniciar(dia, tipo, $event)"
                  @mover="onMover"
                  @soltar="onSoltar(dia, tipo, $event)"
                  @cancelar-puntero="onCancelar"
                  @activar="onActivar(dia, tipo)"
                >
                  <div v-if="editando && !esPasado(dia)" class="d-flex gap-1">
                    <input
                      class="form-control"
                      v-model="dia[tipo]"
                      :aria-label="tipo === 'cena' ? 'Cena' : 'Comida'"
                    />
                    <button
                      type="button"
                      class="btn"
                      :class="dia[`${tipo}Congelada`] ? 'btn-info' : 'btn-outline-secondary'"
                      :aria-pressed="!!dia[`${tipo}Congelada`]"
                      title="Marcar como congelado (avisa el día antes)"
                      aria-label="Marcar como congelado"
                      @click="dia[`${tipo}Congelada`] = !dia[`${tipo}Congelada`]"
                    >
                      <i class="bi bi-snow" aria-hidden="true"></i>
                    </button>
                  </div>
                  <template v-else>
                    <NutricionPlatoLink :texto="dia[tipo]" :href="dia[`${tipo}Href`] || ''" />
                    <i
                      v-if="dia[`${tipo}Congelada`]"
                      class="bi bi-snow text-info ms-1"
                      title="Congelado"
                      aria-label="Congelado"
                    ></i>
                  </template>
                </NutricionPlatoSlot>
              </div>
            </article>
          </div>

          <button
            v-if="editando"
            type="button"
            class="btn btn-primary mt-3 w-100 w-md-auto"
            :disabled="guardando"
            @click="guardar"
          >
            <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
            Guardar cambios
          </button>
        </template>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="fantasma" class="plato-fantasma" :style="fantasma.estilo">
        <div class="plato-fantasma-etiqueta">{{ fantasma.etiqueta }}</div>
        <div class="plato-fantasma-texto">{{ fantasma.texto }}</div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import axios from '@/config/axios';
import NutricionPlatoLink from '@/components/NutricionPlatoLink.vue';
import NutricionPlatoSlot from '@/components/NutricionPlatoSlot.vue';
import {
  capitalizarTexto,
  describirIntercambio,
  diasMenuVisibles,
  esDiaAnterior,
  intercambiarPlatos,
  NUTRICION_DIAS_ANTERIORES,
  platoEsMovible,
} from '@/utils/nutricionMenus';

const TIPOS_PLATO = ['comida', 'cena'];
const UMBRAL_ARRASTRE = 10;

export default {
  name: 'NutricionProximosDias',
  components: { NutricionPlatoLink, NutricionPlatoSlot },
  props: {
    isAdmin: { type: Boolean, default: false },
  },
  setup() {
    const cargando = ref(false);
    const guardando = ref(false);
    const intercambiando = ref(false);
    const error = ref('');
    const aviso = ref('');
    const dias = ref([]);
    const hoy = ref('');
    const actualizadoAt = ref(null);
    const editando = ref(false);
    const snapshot = ref([]);
    const mostrarAnteriores = ref(false);
    const arrastre = ref(null);
    const destinoHover = ref(null);
    const ultimoIntercambio = ref(null);
    let ignorarProximoClick = false;
    let ignorarClickTimer = 0;
    let scrollFrame = 0;

    const diasVisibles = computed(() =>
      diasMenuVisibles(dias.value, hoy.value, mostrarAnteriores.value, NUTRICION_DIAS_ANTERIORES)
    );
    const diasAnterioresCount = computed(
      () => dias.value.filter((d) => esDiaAnterior(d.fecha, hoy.value)).length
    );
    const esPasado = (dia) => esDiaAnterior(dia?.fecha, hoy.value);
    const mostrarAsa = (dia) => !!dia && platoEsMovible({ editando: editando.value });
    const puedeMover = (dia) =>
      !!dia && platoEsMovible({ editando: editando.value, intercambiando: intercambiando.value });

    const formatFecha = (fechaStr) => {
      if (!fechaStr) return '';
      return new Date(`${fechaStr}T00:00:00`).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
      });
    };

    const formatFechaHora = (iso) => {
      if (!iso) return '';
      return new Date(iso).toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const textoAyuda = computed(() => {
      if (intercambiando.value) return 'Guardando cambios…';
      const gesto = arrastre.value;
      if (gesto?.modo === 'arrastrando') {
        return 'Suelta sobre otra comida o cena para intercambiarlas.';
      }
      if (gesto?.modo === 'seleccionado') {
        const dia = dias.value.find((d) => d.fecha === gesto.fecha);
        const turno = gesto.tipo === 'cena' ? 'Cena' : 'Comida';
        const nombre = capitalizarTexto(dia?.weekday || 'día');
        return `${turno} del ${nombre} seleccionada. Toca otra comida o cena para intercambiar, o toca fuera para cancelar.`;
      }
      const base = 'Arrastra una comida o una cena por el asa y suéltala sobre otra para intercambiarlas. En el móvil, toca un asa y después el destino. Pulsa Guardar cambios para confirmar.';
      if (mostrarAnteriores.value && diasAnterioresCount.value) {
        return `${base} También puedes hacerlo con los días anteriores.`;
      }
      return base;
    });

    const fantasma = computed(() => {
      const gesto = arrastre.value;
      if (!gesto || gesto.modo !== 'arrastrando') return null;
      const dia = dias.value.find((d) => d.fecha === gesto.fecha);
      const turno = gesto.tipo === 'cena' ? 'Cena' : 'Comida';
      const arriba = gesto.y > 96;
      return {
        texto: gesto.texto || 'Sin plato',
        etiqueta: `${turno} · ${capitalizarTexto(dia?.weekday || '') || formatFecha(gesto.fecha)}`,
        estilo: {
          left: `${gesto.x}px`,
          top: `${gesto.y}px`,
          transform: arriba ? 'translate(-50%, calc(-100% - 14px))' : 'translate(-50%, 16px)',
        },
      };
    });

    const etiquetaArrastre = (dia, tipo) => {
      const turno = tipo === 'cena' ? 'cena' : 'comida';
      const cuando = `${capitalizarTexto(dia.weekday)} ${formatFecha(dia.fecha)}`.trim();
      const plato = dia[tipo] || 'sin plato';
      if (esOrigen(dia.fecha, tipo) && arrastre.value?.modo === 'seleccionado') {
        return `${capitalizarTexto(turno)} del ${cuando} seleccionada: ${plato}. Toca otra comida o cena para intercambiar.`;
      }
      return `Arrastrar la ${turno} del ${cuando}: ${plato}`;
    };

    const esOrigen = (fecha, tipo) => {
      const gesto = arrastre.value;
      if (!gesto || gesto.fecha !== fecha || gesto.tipo !== tipo) return false;
      return gesto.modo === 'arrastrando' || gesto.modo === 'seleccionado';
    };

    const esDestino = (fecha, tipo) => {
      const destino = destinoHover.value;
      return !!destino && destino.fecha === fecha && destino.tipo === tipo;
    };

    const esPosible = (dia, tipo) => {
      if (!puedeMover(dia)) return false;
      const gesto = arrastre.value;
      if (!gesto || (gesto.modo !== 'arrastrando' && gesto.modo !== 'seleccionado')) return false;
      return !(gesto.fecha === dia.fecha && gesto.tipo === tipo);
    };

    const refrescarMenu = async () => {
      const { data } = await axios.get('/api/nutricion/menu-activo', {
        params: { pasados: NUTRICION_DIAS_ANTERIORES },
      });
      hoy.value = data.hoy || '';
      dias.value = (data.dias || []).map((d) => ({ ...d }));
      actualizadoAt.value = data.actualizadoAt || null;
    };

    const cargar = async ({ silencioso = false } = {}) => {
      if (!silencioso) cargando.value = true;
      error.value = '';
      try {
        await refrescarMenu();
        editando.value = false;
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al cargar el menú de próximos días';
      } finally {
        if (!silencioso) cargando.value = false;
      }
    };

    const toggleEditar = () => {
      arrastre.value = null;
      destinoHover.value = null;
      cerrarArrastreVisual();
      if (editando.value) {
        dias.value = snapshot.value.map((d) => ({ ...d }));
        editando.value = false;
        return;
      }
      aviso.value = '';
      ultimoIntercambio.value = null;
      snapshot.value = dias.value.map((d) => ({ ...d }));
      editando.value = true;
    };

    const guardar = async () => {
      guardando.value = true;
      error.value = '';
      try {
        await axios.put('/api/nutricion/menu-activo', {
          modo: 'editar',
          dias: dias.value,
        });
        await cargar({ silencioso: true });
      } catch (e) {
        error.value = e.response?.data?.error || 'Error al guardar el menú';
      } finally {
        guardando.value = false;
      }
    };

    const pararScroll = () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = 0;
    };

    const bloquearScrollTactil = (event) => {
      if (arrastre.value?.modo === 'arrastrando' && event.cancelable) event.preventDefault();
    };

    const cerrarArrastreVisual = () => {
      pararScroll();
      document.body.classList.remove('nutricion-arrastrando');
      document.removeEventListener('touchmove', bloquearScrollTactil);
    };

    const marcarIgnorarClick = () => {
      ignorarProximoClick = true;
      window.clearTimeout(ignorarClickTimer);
      ignorarClickTimer = window.setTimeout(() => {
        ignorarProximoClick = false;
      }, 500);
    };

    const quitarListenersRaton = () => {
      window.removeEventListener('pointermove', onMover);
      window.removeEventListener('pointerup', onRatonUp);
      window.removeEventListener('pointercancel', onRatonCancel);
    };

    const escucharRaton = () => {
      quitarListenersRaton();
      window.addEventListener('pointermove', onMover);
      window.addEventListener('pointerup', onRatonUp);
      window.addEventListener('pointercancel', onRatonCancel);
    };

    const slotEnPunto = (x, y, origen) => {
      const lista = typeof document.elementsFromPoint === 'function'
        ? document.elementsFromPoint(x, y)
        : [document.elementFromPoint(x, y)].filter(Boolean);
      for (const el of lista) {
        const node = el?.closest?.('[data-plato-fecha][data-plato-slot]');
        if (!node) continue;
        const fecha = node.dataset.platoFecha || '';
        const tipo = node.dataset.platoSlot || '';
        if (tipo !== 'comida' && tipo !== 'cena') continue;
        if (origen && origen.fecha === fecha && origen.tipo === tipo) continue;
        const dia = dias.value.find((d) => d.fecha === fecha);
        if (!dia || !puedeMover(dia)) continue;
        return { fecha, tipo };
      }
      return null;
    };

    const autoScroll = (clientY) => {
      pararScroll();
      const margen = 80;
      let velocidad = 0;
      if (clientY < margen) velocidad = -Math.ceil((margen - clientY) / 6);
      else if (clientY > window.innerHeight - margen) {
        velocidad = Math.ceil((clientY - (window.innerHeight - margen)) / 6);
      }
      if (!velocidad) return;
      const tick = () => {
        const gesto = arrastre.value;
        if (!gesto || gesto.modo !== 'arrastrando') return;
        window.scrollBy(0, velocidad);
        destinoHover.value = slotEnPunto(gesto.x, gesto.y, gesto);
        scrollFrame = requestAnimationFrame(tick);
      };
      scrollFrame = requestAnimationFrame(tick);
    };

    const onIniciar = (dia, tipo, event) => {
      if (!puedeMover(dia)) return;
      const sel = arrastre.value;
      const mismo = !!(sel && sel.fecha === dia.fecha && sel.tipo === tipo);
      arrastre.value = {
        pointerId: event.pointerId,
        fecha: dia.fecha,
        tipo,
        texto: dia[tipo] || '',
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        modo: 'pendiente',
        desdeAsa: !!event.target?.closest?.('.plato-grip'),
        swapFrom: sel?.modo === 'seleccionado' && !mismo
          ? { fecha: sel.fecha, tipo: sel.tipo }
          : null,
        toggleOff: !!(sel?.modo === 'seleccionado' && mismo),
      };
      destinoHover.value = null;
      const tactil = event.pointerType === 'touch' || event.pointerType === 'pen';
      if (!tactil) escucharRaton();
    };

    const onMover = (event) => {
      const gesto = arrastre.value;
      if (!gesto || gesto.pointerId !== event.pointerId || gesto.modo === 'seleccionado') return;
      const dx = event.clientX - gesto.startX;
      const dy = event.clientY - gesto.startY;
      if (gesto.modo === 'pendiente' && Math.hypot(dx, dy) >= UMBRAL_ARRASTRE) {
        gesto.modo = 'arrastrando';
        gesto.swapFrom = null;
        gesto.toggleOff = false;
        document.body.classList.add('nutricion-arrastrando');
        document.addEventListener('touchmove', bloquearScrollTactil, { passive: false });
        window.getSelection?.()?.removeAllRanges?.();
      }
      if (gesto.modo !== 'arrastrando') return;
      if (event.cancelable) event.preventDefault();
      gesto.x = event.clientX;
      gesto.y = event.clientY;
      destinoHover.value = slotEnPunto(event.clientX, event.clientY, gesto);
      autoScroll(event.clientY);
    };

    const intercambiar = (origen, destino, opciones = {}) => {
      if (intercambiando.value) return;
      if (!origen?.fecha || !destino?.fecha || !origen.slot || !destino.slot) return;
      if (origen.fecha === destino.fecha && origen.slot === destino.slot) return;
      const oDia = dias.value.find((d) => d.fecha === origen.fecha);
      const dDia = dias.value.find((d) => d.fecha === destino.fecha);
      if (!puedeMover(oDia) || !puedeMover(dDia)) return;

      const siguiente = intercambiarPlatos(dias.value, origen, destino);
      if (siguiente === dias.value) return;
      dias.value = siguiente;
      error.value = '';
      if (opciones.esDeshacer) {
        aviso.value = 'Cambio deshecho.';
        ultimoIntercambio.value = null;
        return;
      }
      aviso.value = describirIntercambio(
        { ...origen, weekday: oDia?.weekday },
        { ...destino, weekday: dDia?.weekday }
      );
      ultimoIntercambio.value = {
        origen: { fecha: origen.fecha, slot: origen.slot },
        destino: { fecha: destino.fecha, slot: destino.slot },
      };
    };

    const onSoltar = (dia, tipo, event) => {
      const gesto = arrastre.value;
      if (gesto && event && gesto.pointerId === event.pointerId && gesto.modo !== 'seleccionado') {
        cerrarArrastreVisual();
        if (gesto.modo === 'arrastrando') {
          const dest = slotEnPunto(event.clientX, event.clientY, gesto) || destinoHover.value;
          arrastre.value = null;
          destinoHover.value = null;
          marcarIgnorarClick();
          if (dest) {
            intercambiar(
              { fecha: gesto.fecha, slot: gesto.tipo },
              { fecha: dest.fecha, slot: dest.tipo }
            );
          }
          return;
        }
        if (gesto.swapFrom) {
          const origen = gesto.swapFrom;
          arrastre.value = null;
          destinoHover.value = null;
          marcarIgnorarClick();
          intercambiar(
            { fecha: origen.fecha, slot: origen.tipo },
            { fecha: gesto.fecha, slot: gesto.tipo }
          );
          return;
        }
        if (gesto.desdeAsa) {
          if (gesto.toggleOff) {
            arrastre.value = null;
            destinoHover.value = null;
            return;
          }
          arrastre.value = {
            modo: 'seleccionado',
            fecha: gesto.fecha,
            tipo: gesto.tipo,
            texto: gesto.texto,
            pointerId: null,
          };
          return;
        }
        arrastre.value = null;
        destinoHover.value = null;
        return;
      }

      if (gesto?.modo === 'seleccionado' && dia && puedeMover(dia)) {
        if (gesto.fecha === dia.fecha && gesto.tipo === tipo) {
          arrastre.value = null;
          return;
        }
        const origen = { fecha: gesto.fecha, slot: gesto.tipo };
        arrastre.value = null;
        marcarIgnorarClick();
        intercambiar(origen, { fecha: dia.fecha, slot: tipo });
      }
    };

    const onRatonUp = (event) => {
      quitarListenersRaton();
      onSoltar(null, null, event);
    };

    const onCancelar = (event) => {
      const gesto = arrastre.value;
      if (!gesto || (event && gesto.pointerId !== event.pointerId)) return;
      arrastre.value = null;
      destinoHover.value = null;
      cerrarArrastreVisual();
    };

    const onRatonCancel = (event) => {
      quitarListenersRaton();
      onCancelar(event);
    };

    const onActivar = (dia, tipo) => {
      if (!puedeMover(dia)) return;
      const gesto = arrastre.value;
      if (gesto?.modo === 'seleccionado') {
        if (gesto.fecha === dia.fecha && gesto.tipo === tipo) {
          arrastre.value = null;
          return;
        }
        const origen = { fecha: gesto.fecha, slot: gesto.tipo };
        arrastre.value = null;
        intercambiar(origen, { fecha: dia.fecha, slot: tipo });
        return;
      }
      arrastre.value = {
        modo: 'seleccionado',
        fecha: dia.fecha,
        tipo,
        texto: dia[tipo] || '',
        pointerId: null,
      };
    };

    const deshacer = () => {
      const cambio = ultimoIntercambio.value;
      if (!cambio || intercambiando.value) return;
      intercambiar(cambio.origen, cambio.destino, { esDeshacer: true });
    };

    const onDocClick = (event) => {
      if (!ignorarProximoClick) return;
      event.preventDefault();
      event.stopPropagation();
      ignorarProximoClick = false;
      window.clearTimeout(ignorarClickTimer);
    };

    const onDocPointerDown = (event) => {
      const gesto = arrastre.value;
      if (!gesto || gesto.modo !== 'seleccionado') return;
      if (event.target?.closest?.('[data-plato-fecha]')) return;
      arrastre.value = null;
      destinoHover.value = null;
    };

    const onTecla = (event) => {
      if (event.key !== 'Escape' || !arrastre.value) return;
      event.preventDefault();
      arrastre.value = null;
      destinoHover.value = null;
      cerrarArrastreVisual();
      quitarListenersRaton();
    };

    onMounted(() => {
      cargar();
      document.addEventListener('click', onDocClick, true);
      document.addEventListener('pointerdown', onDocPointerDown, true);
      document.addEventListener('keydown', onTecla);
    });

    onUnmounted(() => {
      document.removeEventListener('click', onDocClick, true);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onTecla);
      quitarListenersRaton();
      cerrarArrastreVisual();
      window.clearTimeout(ignorarClickTimer);
    });

    return {
      cargando,
      guardando,
      intercambiando,
      error,
      aviso,
      dias,
      hoy,
      actualizadoAt,
      editando,
      mostrarAnteriores,
      diasVisibles,
      diasAnterioresCount,
      textoAyuda,
      fantasma,
      ultimoIntercambio,
      tiposPlato: TIPOS_PLATO,
      esPasado,
      mostrarAsa,
      esOrigen,
      esDestino,
      esPosible,
      etiquetaArrastre,
      formatFecha,
      formatFechaHora,
      cargar,
      toggleEditar,
      guardar,
      deshacer,
      onIniciar,
      onMover,
      onSoltar,
      onCancelar,
      onActivar,
    };
  },
};
</script>

<style scoped>
.proximos-dnd-ayuda {
  margin: 0 0 0.85rem;
  color: #495057;
  font-size: 0.92rem;
  line-height: 1.4;
}

.proximos-dias-movil {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.proximos-dia-card {
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
  background: #fff;
}

.proximos-dia-card.is-hoy {
  border-color: rgba(13, 110, 253, 0.45);
  background: rgba(13, 110, 253, 0.06);
}

tr.is-pasado > td {
  background-color: #f8f9fa;
}

.proximos-dia-card.is-pasado {
  background: #f8f9fa;
}

.proximos-dia-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.proximos-dia-plan {
  font-size: 0.75rem;
  color: #6c757d;
  text-align: right;
  max-width: 45%;
  word-break: break-word;
}

.proximos-dia-plato {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-top: 0.55rem;
}

.proximos-dia-plato + .proximos-dia-plato {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  margin-top: 0.45rem;
}

.proximos-dia-label {
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6c757d;
}

@media (max-width: 767.98px) {
  .proximos-dias .card-body {
    padding: 0.75rem;
  }
}

@media (min-width: 768px) {
  .w-md-auto {
    width: auto !important;
  }
}
</style>

<style>
body.nutricion-arrastrando {
  user-select: none;
  cursor: grabbing;
}

.plato-fantasma {
  position: fixed;
  z-index: 1080;
  pointer-events: none;
  max-width: min(280px, 72vw);
  padding: 0.45rem 0.7rem;
  border: 1px solid rgba(13, 110, 253, 0.35);
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
}

.plato-fantasma-etiqueta {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0d6efd;
}

.plato-fantasma-texto {
  margin-top: 0.1rem;
  font-size: 0.92rem;
  line-height: 1.3;
  color: #212529;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
